/**
 * v4.30.1 — ShellPreviewCard.
 *
 * Embedded in-modal preview of how an organization's portal shell
 * will render with the just-uploaded logos. Uses the REAL
 * `<PortalBrandMark>` production component with an explicit
 * `override` prop so the mini-shell does NOT hijack any other
 * surface on the page.
 *
 * Three vertically-stacked mini-surfaces shown simultaneously so the
 * operator sees light + dark + topbar in one glance, no toggling
 * required:
 *
 *   ┌────────────────────────────────────┐
 *   │ Sidebar header — light mode        │
 *   ├────────────────────────────────────┤
 *   │ Sidebar header — dark mode         │
 *   ├────────────────────────────────────┤
 *   │ Top command bar — mobile shell     │
 *   └────────────────────────────────────┘
 *
 * The surfaces use the exact same Tailwind classes + background
 * colors as Sidebar.jsx and Layout.jsx so the preview is pixel-
 * faithful to production.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import PortalBrandMark from './PortalBrandMark';

const Row = ({ tone, label, children, stretch = false }) => {
    const isDark = tone === 'dark';
    const surfaceBg = isDark ? '#1e293b' /* slate-800 */ : '#ffffff';
    const borderColor = isDark
        ? 'rgba(148, 163, 184, 0.18)'
        : 'rgba(15, 23, 42, 0.10)';
    const labelColor = isDark ? '#94a3b8' : '#64748b';
    return (
        <div
            className="flex flex-col"
            data-testid={`shell-preview-row-${tone}`}
        >
            <span
                className="text-[10px] tracking-[0.16em] uppercase mb-1.5"
                style={{ color: labelColor }}
            >
                {label}
            </span>
            <div
                /* dark wrapper toggles `.dark` so MutationObservers in
                 * PortalBrandMark pick the dark variant of the logo
                 * inside THIS mini-surface, without flipping the host
                 * document's theme. */
                className={isDark ? 'dark' : ''}
                style={{
                    background: surfaceBg,
                    border: `1px solid ${borderColor}`,
                    borderRadius: 10,
                    padding: stretch ? 0 : 12,
                    overflow: 'hidden',
                }}
            >
                {children}
            </div>
        </div>
    );
};

export const ShellPreviewCard = ({
    /* The branding payload to preview. Shape matches /branding
     * response: { logo_url, logo_dark_url, logo_source, organization,
     * brand_name, ... }. */
    payload,
}) => {
    const { t } = useTranslation();
    /* The preview never makes sense for a totally empty payload, but
     * we still render the rows with the platform fallback so the
     * operator sees that "no logo means YulOnTap stays visible". */
    const safePayload = payload || {
        logo_url: null,
        logo_dark_url: null,
        logo_source: 'platform',
        organization: null,
    };

    /* Synthetic override that always advertises org identity when
     * either variant is present, so PortalBrandMark renders the logo
     * instead of the platform fallback. */
    const overrideForMark = {
        ...safePayload,
        logo_source: (safePayload.logo_url || safePayload.logo_dark_url)
            ? 'org'
            : 'platform',
    };

    return (
        <div
            className="rounded-xl border border-slate-200 dark:border-slate-700 p-4 space-y-3"
            data-testid="shell-preview-card"
        >
            <div>
                <div className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                    {t('portalIdentity.shellPreview.title')}
                </div>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 leading-relaxed">
                    {t('portalIdentity.shellPreview.description')}
                </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Row tone="light" label={t('portalIdentity.shellPreview.sidebarLight')} stretch>
                    {/* Mini sidebar — vertical band with the brand at the
                      * top + 3 placeholder nav items, matching the real
                      * Sidebar.jsx structure visually. */}
                    <div className="flex" style={{ minHeight: 110 }}>
                        <div
                            style={{
                                width: 140,
                                background: '#ffffff',
                                borderRight: '1px solid rgba(15,23,42,0.08)',
                                padding: '12px 12px',
                            }}
                        >
                            <PortalBrandMark
                                variant="sidebar"
                                fallbackToPlatform={true}
                                override={overrideForMark}
                            />
                            <div className="mt-3 space-y-1.5">
                                <div className="h-1.5 rounded-full bg-slate-200" style={{ width: '80%' }} />
                                <div className="h-1.5 rounded-full bg-slate-200" style={{ width: '60%' }} />
                                <div className="h-1.5 rounded-full bg-slate-200" style={{ width: '70%' }} />
                            </div>
                        </div>
                        <div style={{ flex: 1, background: '#f8fafc', padding: '12px' }}>
                            <div className="h-1.5 rounded bg-slate-200 mb-2" style={{ width: '40%' }} />
                            <div className="h-1.5 rounded bg-slate-200" style={{ width: '70%' }} />
                        </div>
                    </div>
                </Row>
                <Row tone="dark" label={t('portalIdentity.shellPreview.sidebarDark')} stretch>
                    <div className="flex" style={{ minHeight: 110 }}>
                        <div
                            style={{
                                width: 140,
                                background: '#1e293b',
                                borderRight: '1px solid rgba(148,163,184,0.18)',
                                padding: '12px 12px',
                            }}
                        >
                            <PortalBrandMark
                                variant="sidebar"
                                fallbackToPlatform={true}
                                override={overrideForMark}
                            />
                            <div className="mt-3 space-y-1.5">
                                <div className="h-1.5 rounded-full bg-slate-600" style={{ width: '80%' }} />
                                <div className="h-1.5 rounded-full bg-slate-600" style={{ width: '60%' }} />
                                <div className="h-1.5 rounded-full bg-slate-600" style={{ width: '70%' }} />
                            </div>
                        </div>
                        <div style={{ flex: 1, background: '#0f172a', padding: '12px' }}>
                            <div className="h-1.5 rounded bg-slate-700 mb-2" style={{ width: '40%' }} />
                            <div className="h-1.5 rounded bg-slate-700" style={{ width: '70%' }} />
                        </div>
                    </div>
                </Row>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Row tone="light" label={t('portalIdentity.shellPreview.topbarLight')} stretch>
                    {/* Mini top command bar — horizontal strip matching
                      * Layout.jsx top-bar dimensions visually. */}
                    <div
                        className="flex items-center justify-between"
                        style={{
                            height: 44,
                            background: '#ffffff',
                            borderBottom: '1px solid rgba(15,23,42,0.08)',
                            padding: '0 12px',
                        }}
                    >
                        <PortalBrandMark
                            variant="topbar"
                            showLabel={false}
                            override={overrideForMark}
                        />
                        <div className="text-[10px] tracking-[0.16em] uppercase text-slate-400">
                            {t('portalIdentity.shellPreview.topbarHelpTheme')}
                        </div>
                    </div>
                </Row>
                <Row tone="dark" label={t('portalIdentity.shellPreview.topbarDark')} stretch>
                    <div
                        className="flex items-center justify-between"
                        style={{
                            height: 44,
                            background: '#1e293b',
                            borderBottom: '1px solid rgba(148,163,184,0.18)',
                            padding: '0 12px',
                        }}
                    >
                        <PortalBrandMark
                            variant="topbar"
                            showLabel={false}
                            override={overrideForMark}
                        />
                        <div className="text-[10px] tracking-[0.16em] uppercase text-slate-500">
                            {t('portalIdentity.shellPreview.topbarHelpTheme')}
                        </div>
                    </div>
                </Row>
            </div>
        </div>
    );
};

/* JSX requires component names to start uppercase. */

export default ShellPreviewCard;
